/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export type TextSpan = { text: string; matched: boolean };

/**
 * This function converts a text into an array of spans indicating what spans matches a given query.
 * @param text Text to be converted.
 * @param query Query to find the matches.
 * @param options Optional: an optional options for the matching. (e.g. if the matching is case sensitive or not)
 */
export const getMatchedSpans = (text: string, query: string, options?: { caseSensitive?: boolean }): TextSpan[] => {
    options = options || {};
    if (!query || !text) {
        return [];
    }

    const q = options.caseSensitive ? query : query.toLocaleLowerCase();
    const t = options.caseSensitive ? text : text.toLocaleLowerCase();

    const spans: TextSpan[] = [];

    // eslint-disable-next-line security/detect-non-literal-regexp
    const regex = new RegExp(q, 'g');
    const matchIndices: [number, number][] = [];

    let match = regex.exec(t);
    while (match) {
        matchIndices.push([match.index, regex.lastIndex]);
        match = regex.exec(t);
    }

    if (matchIndices.length) {
        let i = 0;
        matchIndices.forEach(mi => {
            const [startIdx, endIdx] = mi;
            if (i < startIdx) {
                spans.push({ text: text.substring(i, startIdx), matched: false });
            }
            spans.push({ text: text.substring(startIdx, endIdx), matched: true });
            [, i] = mi;
        });

        if (i < text.length) {
            spans.push({ text: text.substring(i, text.length), matched: false });
        }
    }

    return spans;
};
